/**
 * One place for conversion tracking.
 *
 * Vercel Web Analytics is always on (the script is served first-party from
 * /_vercel/insights/script.js). GA4 is optional: it only exists when
 * REACT_APP_GA_MEASUREMENT_ID is set on the Vercel project, and every call
 * here is a no-op when it is not. PostHog works the same way via
 * REACT_APP_POSTHOG_KEY (see ./posthog.ts).
 */

import { track as vercelTrack } from '@vercel/analytics';
import { posthogCapture, posthogPageView } from './posthog';

type Props = Record<string, string | number | boolean | null>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export const GA_ID: string = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

const gtag = (...args: unknown[]) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    window.gtag(...args);
  } catch {
    /* analytics must never break the page */
  }
};

/** Fire a conversion into Vercel Web Analytics and (when present) GA4. */
export const track = (event: string, props?: Props) => {
  try {
    vercelTrack(event, props ?? undefined);
  } catch {
    /* no-op */
  }
  gtag('event', event, props ?? {});
  posthogCapture(event, props ?? undefined);
};

/** SPA route change - GA4 needs this by hand; Vercel's script does it itself. */
export const trackPageView = (path: string) => {
  posthogPageView(path);
  if (!GA_ID) return;
  gtag('event', 'page_view', {
    page_path: path,
    page_location: typeof window !== 'undefined' ? window.location.href : path,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
  });
};

export const EVENTS = {
  contactSubmitted: 'contact_submitted',
  emailClicked: 'email_clicked',
  phoneClicked: 'phone_clicked',
  ctaClicked: 'cta_clicked',
  adLibraryTab: 'ad_library_tab',
} as const;
